/**
 * BulkResendBar — sticky action strip that appears when the operator has
 * multi-selected one or more DAR / Incident / Maintenance rows and wants to
 * fan out the client-notification email to all of them at once.
 *
 * Backed by `POST /api/reports/{kind}/resend-email/bulk` (iter231). Reports
 * per-id success/failure via toast and clears selection on completion.
 *
 * Rendered inline just below the page header so it doesn't crowd the mobile
 * viewport; slides in when `count > 0`.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Mail, X, Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";

const KIND_LABEL = {
  dar: "DARs",
  incident: "incidents",
  maintenance: "maintenance requests",
};

export default function BulkResendBar({ kind, selected, onClear, testIdPrefix }) {
  const [sending, setSending] = useState(false);
  const count = selected.size;
  if (count === 0) return null;

  const onResend = async () => {
    if (sending) return;
    const ids = Array.from(selected);
    if (!window.confirm(
      `Resend the client-notification email for ${count} ${KIND_LABEL[kind] || "reports"}?`,
    )) return;
    setSending(true);
    try {
      const r = await api.post(`/reports/${kind}/resend-email/bulk`, { ids });
      const { ok = 0, failed = 0, total = ids.length } = r.data || {};
      if (failed === 0) {
        toast.success(`Resent ${ok} of ${total} ${KIND_LABEL[kind] || "reports"}`);
      } else if (ok > 0) {
        toast.warning(`Resent ${ok} · ${failed} failed`, {
          description: "Check server logs for per-record errors.",
        });
      } else {
        toast.error(`All ${total} resends failed`);
      }
      onClear();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk resend failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid={testIdPrefix ? `${testIdPrefix}-bulk-bar` : "bulk-resend-bar"}
      className="sticky top-14 z-30 -mx-8 px-8 py-2.5 border-y border-[#0A84FF]/40 bg-[#0A84FF]/10 backdrop-blur-sm flex items-center gap-4"
    >
      <span className="text-xs uppercase tracking-widest font-mono text-[#0A84FF]">
        {count} selected
      </span>
      <button
        type="button"
        onClick={onResend}
        disabled={sending}
        data-testid={testIdPrefix ? `${testIdPrefix}-bulk-resend` : "bulk-resend-btn"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest border border-[#0A84FF] text-[#0A84FF] hover:bg-[#0A84FF] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {sending ? <Loader2 className="size-3.5 animate-spin" /> : <Mail className="size-3.5" />}
        Resend to portal users
      </button>
      <button
        type="button"
        onClick={onClear}
        disabled={sending}
        data-testid={testIdPrefix ? `${testIdPrefix}-bulk-clear` : "bulk-clear-btn"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-white disabled:opacity-50"
      >
        <X className="size-3.5" /> Clear
      </button>
    </div>
  );
}
